import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import moment from "moment";

function isValidCpf(cpf: string): boolean {
  if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += Number(cpf[i]) * (10 - i);
  }
  const firstDigit = (sum * 10) % 11;
  const normalizedFirstDigit = firstDigit === 10 ? 0 : firstDigit;

  if (normalizedFirstDigit !== Number(cpf[9])) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += Number(cpf[i]) * (11 - i);
  }
  const secondDigit = (sum * 10) % 11;
  const normalizedSecondDigit = secondDigit === 10 ? 0 : secondDigit;

  return normalizedSecondDigit === Number(cpf[10]);
}

function isValidCnpj(cnpj: string): boolean {
  if (!/^\d{14}$/.test(cnpj) || /^(\d)\1{13}$/.test(cnpj)) return false;

  const calcCheckDigit = (base: string, weights: number[]) => {
    const sum = base
      .split("")
      .reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const base = cnpj.slice(0, 12);
  const firstDigit = calcCheckDigit(base, firstWeights);
  const secondDigit = calcCheckDigit(`${base}${firstDigit}`, secondWeights);

  return cnpj.endsWith(`${firstDigit}${secondDigit}`);
}

/**
 * Valida se o valor não excede o tamanho máximo permitido
 */
export function maxLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const value = String(control.value);
    return value.length > max
      ? { maxLength: { max, actual: value.length } }
      : null;
  };
}

/**
 * Valida CPF/CNPJ (aceita 11 ou 14 dígitos com DV válido)
 */
export function cpfCnpjValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) return null;

  const value = String(control.value).replace(/\D/g, "");

  if (value.length === 11) {
    return isValidCpf(value) ? null : { invalidCpfCnpj: true };
  }

  if (value.length === 14) {
    return isValidCnpj(value) ? null : { invalidCpfCnpj: true };
  }

  return { invalidCpfCnpj: true };
}

/**
 * Valida se a data não é anterior à data atual
 */
export function futureDateValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) return null;

  const value = String(control.value).trim();
  const hasTime = /\d{2}:\d{2}/.test(value);
  const date = moment(value, ["DD/MM/YYYY HH:mm", "DD/MM/YYYY"], true);

  if (!date.isValid()) return null;

  const isBeforeNow = hasTime
    ? date.isBefore(moment())
    : date.startOf("day").isBefore(moment().startOf("day"));

  return isBeforeNow ? { pastDate: true } : null;
}

/**
 * Indicador da IE do destinatário (indIEDest) — valores aceitos pelo PlugNotas
 * em `destinatario.indicadorInscricaoEstadual`.
 */
export const IND_IE_DEST = {
  CONTRIBUINTE: "1",
  ISENTO: "2",
  NAO_CONTRIBUINTE: "9"
} as const;

export const IE_ISENTO = "ISENTO";

export type InscricaoEstadualError =
  | "required"
  | "numericOnly"
  | "notAllowed"
  | "invalid";

export const INSCRICAO_ESTADUAL_ERROR_MESSAGES: Record<
  InscricaoEstadualError,
  string
> = {
  required:
    "Informe a inscrição estadual do destinatário (indicador Contribuinte ICMS)",
  numericOnly:
    "Contribuinte ICMS: informe apenas os números da inscrição estadual",
  notAllowed:
    "Destinatário isento ou não contribuinte não deve ter número de inscrição estadual (deixe em branco ou informe ISENTO)",
  invalid: "Inscrição estadual inválida: informe apenas números ou ISENTO"
};

/**
 * Retorna true quando o documento informado é um CNPJ (14 dígitos).
 */
export function isCnpj(cpfCnpj: unknown): boolean {
  return String(cpfCnpj ?? "").replace(/\D/g, "").length === 14;
}

/**
 * Normaliza o indicador de IE para string ("1" | "2" | "9") ou null quando
 * não informado. O valor pode chegar como number (DTO do nf-server / payload
 * da query string) ou string (formulário / orçamento).
 */
export function normalizeIndIeDest(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized ? normalized : null;
}

/**
 * Deriva o indicador de IE a partir da inscrição estadual, replicando a regra
 * que o PlugNotas aplica quando `indicadorInscricaoEstadual` não é enviado
 * (doc: POST /nfe > destinatario.indicadorInscricaoEstadual):
 * 1 quando IE preenchida, 2 quando IE = ISENTO, 9 quando IE não informada.
 */
export function deriveIndIeDest(
  inscricaoEstadual: string | null | undefined
): string {
  const ie = String(inscricaoEstadual ?? "")
    .trim()
    .toUpperCase();

  if (!ie) return IND_IE_DEST.NAO_CONTRIBUINTE;
  if (ie === IE_ISENTO) return IND_IE_DEST.ISENTO;

  return IND_IE_DEST.CONTRIBUINTE;
}

/**
 * Regra única de consistência entre CPF/CNPJ, indicador de IE e IE do
 * destinatário (usada pelo validator do formulário e pelo
 * NfeValidationService):
 * - CPF: IE não se aplica.
 * - Indicador 1 (contribuinte): IE obrigatória e numérica.
 * - Indicador 2 (isento) ou 9 (não contribuinte): IE deve ficar em branco
 *   (ou ISENTO); número de IE não é permitido.
 * - Indicador não informado: o PlugNotas deriva a partir da IE, então só se
 *   exige que a IE, se preenchida, seja numérica ou ISENTO.
 */
export function checkInscricaoEstadualDestinatario(
  cpfCnpj: unknown,
  indicador: unknown,
  inscricaoEstadual: unknown
): InscricaoEstadualError | null {
  if (!isCnpj(cpfCnpj)) return null;

  const ie = String(inscricaoEstadual ?? "")
    .trim()
    .toUpperCase();
  const digits = ie.replace(/\D/g, "");
  const isIsento = ie === IE_ISENTO;
  const isNumeric = !!ie && !isIsento && /^\d+$/.test(ie);

  switch (normalizeIndIeDest(indicador)) {
    case IND_IE_DEST.CONTRIBUINTE:
      if (!ie) return "required";
      return isNumeric ? null : "numericOnly";

    case IND_IE_DEST.ISENTO:
    case IND_IE_DEST.NAO_CONTRIBUINTE:
      if (!ie || isIsento) return null;
      return digits.length ? "notAllowed" : "invalid";

    default:
      if (!ie || isIsento || isNumeric) return null;
      return "invalid";
  }
}

/**
 * Validator do campo `inscricaoEstadual` do formulário do destinatário.
 * Usa as chaves de erro que o <app-input> renderiza ("required" e
 * "customInvalid" + invalidMessage) e expõe o código em
 * `errors.inscricaoEstadual` para a mensagem específica.
 */
export function inscricaoEstadualValidator(
  control: AbstractControl
): ValidationErrors | null {
  const parent = control.parent;
  if (!parent) return null;

  const error = checkInscricaoEstadualDestinatario(
    parent.get("cpfCnpj")?.value,
    parent.get("indicadorInscricaoEstadual")?.value,
    control.value
  );

  if (!error) return null;

  return error === "required"
    ? { required: true, inscricaoEstadual: error }
    : { customInvalid: true, inscricaoEstadual: error };
}

export const FINALIDADE_DEVOLUCAO = "4";

/**
 * Verifica se uma chave de acesso de NF-e (44 dígitos) é estruturalmente
 * válida: somente números, 44 posições e dígito verificador (módulo 11
 * sobre os 43 primeiros dígitos, pesos 2..9 da direita para a esquerda).
 */
export function isValidChaveAcessoNfe(
  value: string | null | undefined
): boolean {
  const chave = String(value ?? "").replace(/\D/g, "");

  if (!/^\d{44}$/.test(chave)) return false;

  const base = chave.slice(0, 43);
  let weight = 2;
  let sum = 0;

  for (let i = base.length - 1; i >= 0; i--) {
    sum += Number(base[i]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }

  const remainder = sum % 11;
  const dv = remainder < 2 ? 0 : 11 - remainder;

  return dv === Number(chave[43]);
}

/**
 * Valida a chave da NF-e referenciada. É obrigatória quando a finalidade da
 * nota é devolução ("4") e, quando informada, precisa ser uma chave válida.
 */
export function chaveNfeReferenciadaValidator(
  control: AbstractControl
): ValidationErrors | null {
  const finalidade = control.parent?.get("finalidade")?.value;
  const isDevolucao = String(finalidade ?? "").trim() === FINALIDADE_DEVOLUCAO;
  const chave = String(control.value ?? "").replace(/\D/g, "");

  // Usa as chaves de erro que o <app-input> já renderiza
  // ("required" e "customInvalid" + invalidMessage).
  if (!chave) {
    return isDevolucao ? { required: true } : null;
  }

  return isValidChaveAcessoNfe(chave) ? null : { customInvalid: true };
}

/**
 * Valida se o código da cidade é um código IBGE válido (7 dígitos)
 */
export function codigoCidadeValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control.value) {
    return { requiredCodigoCidade: true };
  }

  const codigo = String(control.value).replace(/\D/g, "");
  if (!/^\d{7}$/.test(codigo) || Number(codigo) === 0) {
    return { invalidCodigoCidade: true };
  }

  return null;
}
